// Unified Booking Status Configuration
// Used across all components for consistent status display

export const BOOKING_STATUS = {
  REQUESTED: 'requested',
  CONFIRMED: 'confirmed',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  DECLINED: 'declined'
};

// Legacy status mappings (for backward compatibility with old data)
export const mapLegacyStatus = (status) => {
  const mapping = {
    'pending': BOOKING_STATUS.REQUESTED,
    'accepted': BOOKING_STATUS.CONFIRMED,
    'in_progress': BOOKING_STATUS.ACTIVE,
    'completed': BOOKING_STATUS.COMPLETED,
    'cancelled': BOOKING_STATUS.CANCELLED,
    'declined': BOOKING_STATUS.DECLINED,
    'requested': BOOKING_STATUS.REQUESTED
  };
  return mapping[status] || status;
};

// Get status display configuration
export const getStatusConfig = (status) => {
  const normalizedStatus = mapLegacyStatus(status);
  
  const configs = {
    [BOOKING_STATUS.REQUESTED]: {
      label: 'Pending',
      icon: '⏳',
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      description: 'Awaiting caregiver confirmation',
      descriptionForCaregiver: 'New booking request - action required',
      descriptionForOwner: 'Waiting for caregiver response'
    },
    [BOOKING_STATUS.CONFIRMED]: {
      label: 'Confirmed',
      icon: '✅',
      color: 'bg-green-100 text-green-800 border-green-200',
      description: 'Booking confirmed',
      descriptionForCaregiver: 'Booking confirmed - scheduled',
      descriptionForOwner: 'Booking confirmed - scheduled'
    },
    [BOOKING_STATUS.ACTIVE]: {
      label: 'Active',
      icon: '🔵',
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      description: 'Service currently in progress',
      descriptionForCaregiver: 'Service in progress',
      descriptionForOwner: 'Service in progress'
    },
    [BOOKING_STATUS.COMPLETED]: {
      label: 'Completed',
      icon: '⭐',
      color: 'bg-green-100 text-green-800 border-green-200',
      description: 'Service completed successfully',
      descriptionForCaregiver: 'Service completed successfully',
      descriptionForOwner: 'Service completed successfully'
    },
    [BOOKING_STATUS.CANCELLED]: {
      label: 'Cancelled',
      icon: '❌',
      color: 'bg-red-100 text-red-800 border-red-200',
      description: 'Booking was cancelled',
      descriptionForCaregiver: 'Booking was cancelled',
      descriptionForOwner: 'Booking was cancelled'
    },
    [BOOKING_STATUS.DECLINED]: {
      label: 'Declined',
      icon: '⛔',
      color: 'bg-red-100 text-red-800 border-red-200',
      description: 'Request was declined',
      descriptionForCaregiver: 'You declined this request',
      descriptionForOwner: 'Request declined by caregiver'
    }
  };

  return configs[normalizedStatus] || {
    label: normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1),
    icon: '📋',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    description: normalizedStatus,
    descriptionForCaregiver: normalizedStatus,
    descriptionForOwner: normalizedStatus
  };
};

// Get all available statuses for filters
export const getAllStatuses = () => {
  return [
    { value: 'all', label: 'All Bookings' },
    { value: BOOKING_STATUS.REQUESTED, label: 'Pending' },
    { value: BOOKING_STATUS.CONFIRMED, label: 'Confirmed' },
    { value: BOOKING_STATUS.ACTIVE, label: 'Active' },
    { value: BOOKING_STATUS.COMPLETED, label: 'Completed' },
    { value: BOOKING_STATUS.CANCELLED, label: 'Cancelled' },
    { value: BOOKING_STATUS.DECLINED, label: 'Declined' }
  ];
};

// Check if status allows caregiver actions (accept/decline)
export const canCaregiverRespond = (status) => {
  const normalizedStatus = mapLegacyStatus(status);
  return normalizedStatus === BOOKING_STATUS.REQUESTED;
};

// Get status badge component props
export const getStatusBadgeProps = (status) => {
  const config = getStatusConfig(status);
  return {
    className: `inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${config.color}`,
    children: `${config.icon} ${config.label}`
  };
};

